<template>
	<v-row no-gutters class="pa-2 overflow-auto" style="height: 100%">
		<!-- Time tracking of the user in this page -->
		<UserTimeTracker page-key="ccmRecordInfo" />

		<v-col v-if="!currentRecord">
			<v-card style="width: 100%; height: 100%" class="text-center d-flex flex-column justify-center align-center">
				<v-icon color="grey" size="50" class="mb-3">mdi-alert-circle-outline</v-icon>
				<p class="font-responsive-lg lightgrey">
					{{ $t('no-data-available') }}
				</p>
				<p class="font-responsive-md lightgrey">
					{{ $t('select-or-add-record') }}
				</p>
			</v-card>
		</v-col>

		<!-- Loading skeleton -->
		<v-row v-else-if="dataLoading" no-gutters style="height: 100%; min-height: 624px">
			<v-col cols="5" class="pa-1 overflow-hidden" style="height: 100%; min-height: 512px">
				<v-row dense class="pa-1" elevation="1" style="height: 44%">
					<v-card height="24vh" style="width: 100%">
						<v-skeleton-loader class="mx-auto" type="card-heading, list-item-two-line@5" />
					</v-card>
				</v-row>

				<v-row no-gutters class="pt-3" style="height: 56%; min-height: 200px">
					<v-card height="15vh">
						<v-skeleton-loader class="mx-auto" type="card-heading, table" :v-bind="{ elevation: 1 }" />
					</v-card>
				</v-row>
			</v-col>

			<v-col cols="7" class="pa-1" style="height: 100%">
				<v-card class="pa-5 overflow-hidden" style="width: 100%; height: 100%">
					<v-skeleton-loader class="mx-auto" type="card-heading" :v-bind="{ elevation: 1 }" />
					<v-card height="45vh" class="mb-3">
						<v-skeleton-loader class="mx-auto" type="card-heading" :v-bind="{ elevation: 1 }" />
						<v-skeleton-loader class="mx-auto" type="card-header, list-item-three-line@4 " :v-bind="{ elevation: 1 }" />
					</v-card>
					<v-card height="20vh">
						<v-skeleton-loader class="mx-auto" type="chip" :v-bind="{ elevation: 1 }" />
					</v-card>
				</v-card>
			</v-col>
		</v-row>

		<v-row v-else no-gutters style="height: 100%; min-height: 624px">
			<v-col lg="5" cols="12" class="pa-1 d-flex flex-column" style="height: 100%; min-height: 608px">
				<v-row dense class="ma-0 flex-grow-0" ref="ccmRecordInfo">
					<v-card style="width: 100%; height: 100%; min-height: 200px">
						<v-card-title class="py-2 py-lg-4">
							<v-row no-gutters>
								<v-col cols="6" class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
									{{ $t('info') }}
								</v-col>
								<v-spacer />
								<v-col cols="1">
									<v-icon
										color="primary"
										@click="synchronizeEditDrawers('CCMInfoEdit')"
										:size="$vuetify.breakpoint.mdAndDown ? 20 : 25">
										mdi-pencil
									</v-icon>
								</v-col>
							</v-row>
						</v-card-title>
						<v-card-text
							class="overflow-y-auto"
							:style="$vuetify.breakpoint.mdAndDown ? 'height: calc(100% - 48px)' : 'height: calc(100% - 64px)'">
							<v-row v-for="(detail, index) in detailsList" :key="index" no-gutters class="pb-1">
								<v-col>
									<p class="text-b1 color-black text-left ma-0">
										{{ $t(detail[0]) }}
									</p>
								</v-col>
								<v-col>
									<p class="text-b1 text-right ma-0">
										{{ detail[1] }}
									</p>
								</v-col>
							</v-row>

							<v-row>
								<v-col cols="7">
									<v-menu
										ref="menu"
										v-model="menu"
										:close-on-content-click="false"
										transition="scale-transition"
										offset-y
										min-width="auto">
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												v-model="monthYearSelected"
												:label="$t('month')"
												prepend-icon="mdi-calendar"
												readonly
												dense
												outlined
												hide-details
												v-bind="attrs"
												v-on="on" />
										</template>
										<v-date-picker
											v-model="monthYearSelected"
											type="month"
											no-title
											scrollable
											color="primary"
											@input="menu = false">
										</v-date-picker>
									</v-menu>
								</v-col>
								<v-col cols="5" class="justify-end d-flex align-center">
									<v-btn color="primary" @click="showDialog">Generate</v-btn>
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>
				</v-row>

				<v-row no-gutters class="pt-3" :style="calculateFunctionalStatusHeight">
					<CCMFunctionalStatus />
				</v-row>
			</v-col>
			<v-col lg="7" cols="12" class="pa-1" style="height: 100%; min-height: 608px">
				<v-row no-gutters style="height: 55%" class="pb-3">
					<CCMDiagnosis />
				</v-row>
				<v-row no-gutters style="height: 45%">
					<CCMAllergy />
				</v-row>
			</v-col>
		</v-row>
		<CCMMonthlyReport
			:monthYearSelected="monthYearSelected"
			:dialog.sync="showGenerateMonthlyDialog"
			v-if="showGenerateMonthlyDialog" />
	</v-row>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { formatTimestamp } from '@/utils/utilities';
import CCMFunctionalStatus from '@/views/Client/components/ChronicCareManagement/CCMFunctionalStatus.vue';
import CCMDiagnosis from '@/views/Client/components/ChronicCareManagement/CCMDiagnosis.vue';
import CCMAllergy from '@/views/Client/components/ChronicCareManagement/CCMAllergy.vue';
import CCMMonthlyReport from '../ChronicCareManagement/CCMMonthlyReport.vue';
import UserTimeTracker from '@/views/Client/components/UserTimeTracker.vue';

export default {
	name: 'CCMRecordInfo',
	components: {
		CCMFunctionalStatus,
		CCMDiagnosis,
		CCMAllergy,
		CCMMonthlyReport,
		UserTimeTracker,
	},

	data () {
		return {
			monthYearSelected: new Date().toISOString().substr(0, 7),
			menu: false,
			modal: false,
			showGenerateMonthlyDialog: false,
			infoHeight: 0,
		};
	},

	computed: {
		...mapState({
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			dataLoading: (state) => state.ccmRecords.ccmRecordLoadingStatus,
			detailedUser: (state) => state.users.detailedUser,
		}),

		detailsList () {
			const list = [
				['provider', this.currentRecord.providerName],
				['coordinator', this.currentRecord.coordinatorName],
				['date-of-service', this.formatTimestamp(this.currentRecord.dateOfService, 'date_med')],
				['last-modified-on', this.formatTimestamp(this.currentRecord.lastModifiedAt, 'datetime_med')],
				['last-modified-by', this.currentRecord.lastModifiedBy],
			];
			if (this.currentRecord.isComplex) {
				list.splice(2, 0, ['ccm-type', this.$t('complex')]);
			} else if (this.currentRecord.isNonComplex) {
				list.splice(2, 0, ['ccm-type', this.$t('non-complex')]);
			} else {
				list.splice(2, 0, ['ccm-type', this.$t('physician-npp-provided')]);
			}
			return list;
		},

		calculateFunctionalStatusHeight () {
			if (this.infoHeight <= 300) return 'height: calc(100% - 312px)';
			return 'height: calc(100% - ' + (this.infoHeight + 12) + 'px)';
		},
	},

	methods: {
		...mapActions({
			deleteCCMDiagnosis: 'ccmDiagnoses/deleteCCMDiagnosis',
			getSelectedCCMRecordId: 'ccmRecords/getSelectedCCMRecordId',
		}),
		formatTimestamp,
		synchronizeEditDrawers (drawer) {
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'ccmDetails',
			});
		},
		showDialog () {
			this.showGenerateMonthlyDialog = true;
		},
		onWindowResize () {
			// this.infoHeight = this.$refs.ccmRecordInfo.clientHeight;
		},
	},
	mounted () {
		window.addEventListener('resize', this.onWindowResize);
		this.$nextTick(() => {
			this.onWindowResize();
		});
	},
	beforeDestroy () {
		window.removeEventListener('resize', this.onWindowResize);
	},
};
</script>
